import React, { useEffect, useState } from 'react';
import {
    Form,
    Input,
    Button,
    InputNumber,
    Select,
    Typography,
    Space,
    Divider,
    Checkbox,
    Spin,
    Tag,
    Descriptions,
    Alert,
    Collapse,
    Modal
} from 'antd';
import {
    PlusOutlined,
    CrownOutlined,
    ShoppingOutlined,
    TagOutlined,
    PlayCircleOutlined,
    SettingOutlined,
    InfoCircleOutlined,
    LockOutlined,
    UnlockOutlined,
    CalendarOutlined,
    HistoryOutlined,
    FireOutlined
} from '@ant-design/icons';

import { videoUrlValidator } from '../../utils/videoUtils';
import { computeMinIncrement } from '../../utils/pricing';
import VideoPreviewModal from '../VideoPreviewModal';

const { Title, Text } = Typography;
const { TextArea } = Input;

const LotForm = ({
    form,
    loading,
    isEdit,
    auctions = [],
    auctionIdFromUrl,
    onAuctionChange,
    onManualLoadSpecifications,
    renderSpecificationGroup,
    onSubmit,
    disabled = false
}) => {
    const [videoModalVisible, setVideoModalVisible] = useState(false);
    const selectedAuctionId = Form.useWatch('auction_id', form);
    const videoUrl = Form.useWatch('video_url', form);
    const isReserve = Form.useWatch('is_reserve', form);
    const canBuyNow = Form.useWatch('can_buy_now', form);
    const selectedAuction = auctions.find(a => a.id === selectedAuctionId);
    const prevAuctionIdRef = React.useRef(selectedAuctionId);
    // While creating a new lot, auto-fill the bid increment from the starting price
    // until the user manually edits it; when editing an existing lot, leave it alone.
    const incrementTouchedRef = React.useRef(isEdit);

    // Handle auction change with confirmation
    const handleAuctionSelect = (newAuctionId) => {
        if (isEdit || !prevAuctionIdRef.current || prevAuctionIdRef.current === newAuctionId) {
            // In edit mode or first load or same selection, just change
            form.setFieldValue('auction_id', newAuctionId);
            if (onAuctionChange) onAuctionChange(newAuctionId);
            prevAuctionIdRef.current = newAuctionId;
            return;
        }
        Modal.confirm({
            title: 'Change Auction/Category?',
            content: 'Changing the auction or category will clear all current specifications and load new ones for the selected category. Do you want to continue?',
            okText: 'Yes, change',
            cancelText: 'No, keep current',
            onOk: () => {
                // Clear specifications before loading new ones
                form.setFieldValue('specifications', [{ heading: '', fields: [{ key: '', value: '' }] }]);
                form.setFieldValue('auction_id', newAuctionId);
                if (onAuctionChange) onAuctionChange(newAuctionId);
                prevAuctionIdRef.current = newAuctionId;
            },
            onCancel: () => {
                // Revert selection
                form.setFieldValue('auction_id', prevAuctionIdRef.current);
            }
        });
    };

    useEffect(() => {
        prevAuctionIdRef.current = selectedAuctionId;
    }, [selectedAuctionId]);

    return (
        <Spin spinning={loading}>
        <Form
            form={form}
            layout="vertical"
            onFinish={onSubmit}
            initialValues={{
                specifications: [{ heading: '', fields: [{ key: '', value: '' }] }]
            }}
            className="lot-form"
        >
            <Form.Item
                name="auction_id"
                label="Auction"
                rules={[{ required: true, message: 'Please select an auction to continue' }]}
            >
                <Select
                    placeholder="Search and select an auction..."
                    showSearch
                    optionFilterProp="children"
                    disabled={disabled || isEdit || !!auctionIdFromUrl}
                    style={{ width: '100%', borderRadius: '8px' }}
                    loading={loading}
                    onChange={handleAuctionSelect}
                    value={selectedAuctionId}
                    size="large"
                    suffixIcon={<TagOutlined style={{ color: '#1890ff' }} />}
                >
                    {Array.isArray(auctions) && auctions.map(auction => (
                        <Select.Option key={auction.id} value={auction.id}>
                            <Space>
                                <span style={{ fontWeight: 500 }}>{auction.title}</span>
                                <Tag color={getStatusColor(auction.status)} style={{ borderRadius: '4px' }}>
                                    {auction.status?.toUpperCase()}
                                </Tag>
                            </Space>
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>

            {selectedAuction && (
                <div style={{ marginBottom: 24 }}>
                    <Descriptions column={1} size="small" bordered>
                        <Descriptions.Item label="Auction Title">
                            <span style={{ fontWeight: 600 }}>{selectedAuction.title}</span>
                        </Descriptions.Item>
                    </Descriptions>
                    <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered style={{ marginTop: 0 }}>
                        <Descriptions.Item label="Type">
                            <Space size="small" wrap>
                                <Tag color={selectedAuction.is_private ? 'blue' : 'green'} icon={selectedAuction.is_private ? <LockOutlined /> : <UnlockOutlined />}>
                                    {selectedAuction.is_private ? 'Private' : 'Public'}
                                </Tag>
                                <Tag color={selectedAuction.is_sequential ? 'purple' : 'orange'} icon={selectedAuction.is_sequential ? <HistoryOutlined /> : <FireOutlined />}>
                                    {selectedAuction.is_sequential ? 'Sequential' : 'Live'}
                                </Tag>
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Category">
                            <Space>
                                <span dangerouslySetInnerHTML={{ __html: selectedAuction.category?.icon || '🏷️' }} />
                                <span>{selectedAuction.category?.name || 'Uncategorized'}</span>
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Start Date">
                            <Space>
                                <CalendarOutlined style={{ color: '#1890ff' }} />
                                <span>{new Date(selectedAuction.start_time).toLocaleDateString()}</span>
                            </Space>
                        </Descriptions.Item>
                    </Descriptions>
                    <Alert
                        message={isEdit ? 'Updating Lot' : 'Creating New Lot'}
                        description={
                            isEdit
                                ? `You're updating the lot details for "${selectedAuction.title}".`
                                : `You're creating a new lot for "${selectedAuction.title}".`
                        }
                        type="info"
                        showIcon
                        icon={<InfoCircleOutlined />}
                        style={{ marginTop: 12, borderRadius: '8px' }}
                    />
                </div>
            )}

            <Title level={4} style={{ margin: '24px 0 16px' }}>Lot Details</Title>

            <Form.Item
                name="title"
                label="Lot Title"
                rules={[{ required: true, message: 'A title is required' }]}
            >
                <Input
                    placeholder="e.g., Premium Quality Horse - Well Trained, 5 Years Old"
                    maxLength={100}
                    showCount
                    disabled={disabled}
                    size="large"
                    style={{ borderRadius: '8px' }}
                />
            </Form.Item>

            <Form.Item
                name="description"
                label="Description"
                rules={[{ required: true, message: 'Please provide a description' }]}
            >
                <TextArea
                    rows={4}
                    placeholder="Describe your lot: condition, history, notable features."
                    maxLength={1000}
                    showCount
                    disabled={disabled}
                    size="large"
                    style={{ borderRadius: '8px' }}
                />
            </Form.Item>

            <Form.Item
                name="starting_price"
                label="Starting Price"
                dependencies={['reserve_price', 'is_reserve']}
                rules={[
                    { required: true, message: 'Please set a starting price' },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value) return Promise.resolve();
                            const reservePrice = getFieldValue('reserve_price');
                            const isReserve = getFieldValue('is_reserve');
                            if (isReserve && reservePrice && parseFloat(value) >= parseFloat(reservePrice)) {
                                return Promise.reject('Starting price must be less than the reserve price');
                            }
                            return Promise.resolve();
                        },
                    }),
                ]}
            >
                <InputNumber
                    min={0}
                    max={999999999999999999.99}
                    step={0.01}
                    prefix={<span style={{ color: '#52c41a', fontWeight: 500 }}>PKR</span>}
                    style={{ width: '100%', borderRadius: '8px' }}
                    placeholder="0.00"
                    onChange={(value) => {
                        if (!incrementTouchedRef.current) {
                            form.setFieldValue('default_increment', computeMinIncrement(value));
                        }
                        form.validateFields(['starting_price', 'reserve_price', 'default_increment']);
                    }}
                    disabled={disabled}
                    size="large"
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                />
            </Form.Item>

            <Form.Item
                name="quantity"
                label="Quantity"
                rules={[{ required: true, message: 'Please specify how many items are in this lot' }]}
            >
                <InputNumber
                    min={1}
                    style={{ width: '100%', borderRadius: '8px' }}
                    placeholder="e.g., 1, 5, 10"
                    disabled={disabled}
                    size="large"
                />
            </Form.Item>

            <Form.Item
                name="default_increment"
                label="Default Bid Increment"
                dependencies={['reserve_price', 'is_reserve', 'starting_price']}
                rules={[
                    { required: true, message: 'Please set the default bid increment amount' },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value) return Promise.resolve();
                            const minIncrement = computeMinIncrement(getFieldValue('starting_price'));
                            if (parseFloat(value) < minIncrement) {
                                return Promise.reject(`Must be at least PKR ${minIncrement} (5% of the starting price)`);
                            }
                            const isReserve = getFieldValue('is_reserve');
                            const reservePrice = getFieldValue('reserve_price');
                            if (isReserve && reservePrice && parseFloat(value) === parseFloat(reservePrice)) {
                                return Promise.reject('Default bid increment cannot be the same as the reserve price');
                            }
                            return Promise.resolve();
                        },
                    }),
                ]}
                extra={
                    <Text type="secondary" style={{ fontSize: '13px' }}>
                        The base amount used for quick bid buttons (e.g. Bid x1, x2, x5). Defaults to 5% of the starting price, and can't be set lower than that.
                    </Text>
                }
            >
                <InputNumber
                    min={1}
                    step={1}
                    prefix={<span style={{ color: '#1890ff', fontWeight: 500 }}>PKR</span>}
                    style={{ width: '100%', borderRadius: '8px' }}
                    placeholder="e.g., 5, 10, 50"
                    onChange={() => {
                        incrementTouchedRef.current = true;
                        form.validateFields(['default_increment', 'reserve_price']);
                    }}
                    disabled={disabled}
                    size="large"
                />
            </Form.Item>

            <Divider style={{ margin: '24px 0' }} />

            <Title level={4} style={{ marginBottom: 8 }}>Item Specifications</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: '13px' }}>
                Group related details (e.g. "Physical Details", "Condition & History") to help bidders decide.
            </Text>

            <Form.List
                name="specifications"
                rules={[
                    {
                        validator: async (_, specifications) => {
                            if (!specifications || specifications.length < 1) {
                                return Promise.reject(new Error('Please add at least one specification group'));
                            }
                        },
                    },
                ]}
            >
                {(fields, { add, remove }, { errors }) => (
                    <>
                        {fields.map((field) => renderSpecificationGroup(field, { add, remove }, { errors }))}

                        <Button
                            type="dashed"
                            onClick={() => add({ heading: '', fields: [{ key: '', value: '' }] })}
                            block
                            icon={<PlusOutlined />}
                            size="large"
                            style={{
                                marginTop: 16,
                                borderRadius: '8px',
                                height: '48px'
                            }}
                        >
                            Add Another Specification Group
                        </Button>
                        {errors.length > 0 && (
                            <div style={{ marginTop: 12, color: '#ff4d4f', fontSize: '14px' }}>
                                {errors[0]}
                            </div>
                        )}
                    </>
                )}
            </Form.List>

            <Divider style={{ margin: '24px 0' }} />

            <Collapse
                bordered={false}
                style={{ background: 'transparent' }}
                items={[
                    {
                        key: 'advanced',
                        forceRender: true,
                        label: (
                            <Space>
                                <SettingOutlined />
                                <span style={{ fontWeight: 600, fontSize: 15 }}>Advanced Options (optional)</span>
                            </Space>
                        ),
                        children: (
                            <div>
                                {!canBuyNow && (
                                    <>
                                        <Form.Item name="is_reserve" valuePropName="checked" style={{ marginBottom: 8 }}>
                                            <Checkbox
                                                onChange={(e) => {
                                                    if (!e.target.checked) {
                                                        form.setFieldValue('reserve_price', null);
                                                        form.setFieldValue('is_reserve', false);
                                                    } else {
                                                        form.setFieldValue('is_reserve', true);
                                                        form.setFieldValue('can_buy_now', false);
                                                        form.setFieldValue('buy_now_price', null);
                                                    }
                                                }}
                                                disabled={disabled}
                                            >
                                                <Space size="small">
                                                    <CrownOutlined style={{ color: '#faad14' }} />
                                                    <span style={{ fontWeight: 600 }}>Enable Reserve Price</span>
                                                </Space>
                                            </Checkbox>
                                        </Form.Item>
                                        <Text type="secondary" style={{ fontSize: '13px', display: 'block', marginBottom: 16 }}>
                                            Your minimum acceptable price — the lot won't sell below it. Cannot be combined with Buy Now.
                                        </Text>
                                    </>
                                )}

                                {isReserve && (
                                    <Form.Item
                                        name="reserve_price"
                                        label="Reserve Price"
                                        dependencies={['starting_price', 'is_reserve', 'default_increment']}
                                        rules={[
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    const isReserveVal = getFieldValue('is_reserve');
                                                    if (isReserveVal && (!value || value === '')) {
                                                        return Promise.reject('Please set your reserve price');
                                                    }
                                                    if (value && value !== '') {
                                                        const startingPrice = getFieldValue('starting_price');
                                                        if (startingPrice && parseFloat(value) <= parseFloat(startingPrice)) {
                                                            return Promise.reject('Reserve price must be greater than the starting price');
                                                        }
                                                        const defaultIncrement = getFieldValue('default_increment');
                                                        if (defaultIncrement && parseFloat(value) === parseFloat(defaultIncrement)) {
                                                            return Promise.reject('Reserve price cannot be the same as the default bid increment');
                                                        }
                                                    }
                                                    return Promise.resolve();
                                                },
                                            }),
                                        ]}
                                    >
                                        <InputNumber
                                            min={0}
                                            max={999999999999999999.99}
                                            step={0.01}
                                            prefix={<span style={{ color: '#faad14', fontWeight: 500 }}>PKR</span>}
                                            style={{ width: '100%', borderRadius: '8px' }}
                                            placeholder="Enter your minimum price"
                                            onBlur={(e) => {
                                                const value = e.target.value;
                                                if (!value || value === '') {
                                                    form.setFieldValue('reserve_price', null);
                                                }
                                            }}
                                            onChange={(value) => {
                                                if (value === null || value === '') {
                                                    form.setFieldValue('reserve_price', null);
                                                }
                                                form.validateFields(['starting_price', 'reserve_price', 'default_increment']);
                                            }}
                                            disabled={disabled}
                                            size="large"
                                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                        />
                                    </Form.Item>
                                )}

                                <Divider style={{ margin: '20px 0' }} />

                                {!isReserve && (
                                    <>
                                        <Form.Item name="can_buy_now" valuePropName="checked" style={{ marginBottom: 8 }}>
                                            <Checkbox
                                                onChange={(e) => {
                                                    if (!e.target.checked) {
                                                        form.setFieldValue('buy_now_price', null);
                                                        form.setFieldValue('can_buy_now', false);
                                                    } else {
                                                        form.setFieldValue('can_buy_now', true);
                                                        form.setFieldValue('is_reserve', false);
                                                        form.setFieldValue('reserve_price', null);
                                                    }
                                                }}
                                                disabled={disabled}
                                            >
                                                <Space size="small">
                                                    <ShoppingOutlined style={{ color: '#52c41a' }} />
                                                    <span style={{ fontWeight: 600 }}>Enable Buy Now Option</span>
                                                </Space>
                                            </Checkbox>
                                        </Form.Item>
                                        <Text type="secondary" style={{ fontSize: '13px', display: 'block', marginBottom: 16 }}>
                                            Let buyers purchase instantly at a fixed price, skipping the auction. Cannot be combined with Reserve Price.
                                        </Text>

                                        {canBuyNow && (
                                            <Form.Item
                                                name="buy_now_price"
                                                label="Buy Now Price"
                                                dependencies={['starting_price', 'can_buy_now']}
                                                rules={[
                                                    ({ getFieldValue }) => ({
                                                        validator(_, value) {
                                                            const canBuyNowVal = getFieldValue('can_buy_now');
                                                            if (canBuyNowVal && (!value || value === '')) {
                                                                return Promise.reject('Please set your Buy Now price');
                                                            }
                                                            if (value && value !== '') {
                                                                const startingPrice = getFieldValue('starting_price');
                                                                if (startingPrice && parseFloat(value) < parseFloat(startingPrice)) {
                                                                    return Promise.reject('Buy Now price must be greater than or equal to the starting price');
                                                                }
                                                            }
                                                            return Promise.resolve();
                                                        },
                                                    }),
                                                ]}
                                            >
                                                <InputNumber
                                                    min={0}
                                                    max={999999999999999999.99}
                                                    step={0.01}
                                                    prefix={<span style={{ color: '#52c41a', fontWeight: 500 }}>PKR</span>}
                                                    style={{ width: '100%', borderRadius: '8px' }}
                                                    placeholder="Enter instant purchase price"
                                                    onBlur={(e) => {
                                                        const value = e.target.value;
                                                        if (!value || value === '') {
                                                            form.setFieldValue('buy_now_price', null);
                                                        }
                                                    }}
                                                    onChange={(value) => {
                                                        if (value === null || value === '') {
                                                            form.setFieldValue('buy_now_price', null);
                                                        }
                                                        form.validateFields(['starting_price', 'buy_now_price']);
                                                    }}
                                                    disabled={disabled}
                                                    size="large"
                                                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                    parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                                                />
                                            </Form.Item>
                                        )}

                                        <Divider style={{ margin: '20px 0' }} />
                                    </>
                                )}

                                <Form.Item
                                    name="video_url"
                                    label="Promotional Video URL"
                                    rules={[{ validator: videoUrlValidator }]}
                                    extra={
                                        <Space direction="vertical" size={8}>
                                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                                Paste a YouTube, Vimeo, or TikTok link to showcase this item.
                                            </Text>
                                            {videoUrl && (
                                                <Button
                                                    type="dashed"
                                                    icon={<PlayCircleOutlined />}
                                                    onClick={() => setVideoModalVisible(true)}
                                                >
                                                    Preview Video
                                                </Button>
                                            )}
                                        </Space>
                                    }
                                >
                                    <Input
                                        placeholder="https://youtube.com/watch?v=..."
                                        prefix={<PlayCircleOutlined style={{ color: '#1890ff' }} />}
                                        disabled={disabled}
                                        size="large"
                                        style={{ borderRadius: '8px' }}
                                    />
                                </Form.Item>

                                <VideoPreviewModal
                                    visible={videoModalVisible}
                                    onClose={() => setVideoModalVisible(false)}
                                    videoUrl={videoUrl}
                                    title="Lot Video Preview"
                                    width={900}
                                    height={500}
                                />
                            </div>
                        )
                    }
                ]}
            />
        </Form>
        </Spin>
    );
};

const getStatusColor = (status) => {
    const colors = {
        draft: 'default',
        scheduled: 'processing',
        active: 'success',
        completed: 'success',
        cancelled: 'error'
    };
    return colors[status] || 'default';
};

export const DuplicateLotModal = ({
    open,
    title,
    onTitleChange,
    onCancel,
    onConfirm,
    loading = false,
}) => (
    <Modal
        title="Duplicate Lot"
        open={open}
        onCancel={onCancel}
        onOk={onConfirm}
        okText="Duplicate"
        confirmLoading={loading}
        destroyOnClose
    >
        <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
                Enter a title for the new lot. A unique URL slug will be generated automatically. Images are not copied.
            </Text>
        </div>
        <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Lot Title <Text type="danger">*</Text>
            </Text>
            <Input
                value={title}
                onChange={(e) => onTitleChange(e.target.value)}
                placeholder="Enter lot title"
                maxLength={255}
                onPressEnter={onConfirm}
                disabled={loading}
            />
        </div>
    </Modal>
);

export default LotForm;
